import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { useInvalidateAccountSettingsQuery } from '@/entities/account';
import { useInvalidateProfileQuery } from '@/entities/profile';
import { patchChangePassword } from '@/shared/api/auth';
import type { GoChatBackendInternalModelsChangePasswordRequest } from '@/shared/api/auth/generate/auth.gen';
import { useNotifications } from '@/shared/ui/Notifications';

import { ChangePasswordFormDataType } from './types';

export function useChangePassword() {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();
  const invalidateAccountSettings = useInvalidateAccountSettingsQuery();
  const invalidateProfile = useInvalidateProfileQuery();

  const changePasswordMutation = useMutation({
    mutationFn: (vars: GoChatBackendInternalModelsChangePasswordRequest) =>
      patchChangePassword(vars),
    onSuccess: async () => {
      await Promise.all([invalidateAccountSettings(), invalidateProfile()]);

      addNotification({
        type: 'success',
        title: t('settings:changePassword.notifications.success'),
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: error.message,
        caption: error.data?.details,
      });
    },
  });

  const changeEmail = async ({
    newPassword,
    currentPassword,
  }: ChangePasswordFormDataType) => {
    await changePasswordMutation.mutateAsync({
      new_password: newPassword,
      current_password: currentPassword,
    });
  };

  return {
    changePassword: changeEmail,
    ...changePasswordMutation,
  };
}
